import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { watchlistActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { LoadPanel } from 'devextreme-react/load-panel';
import debounce from 'lodash.debounce';
import { WatchlistApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import DataGrid, { Column, Paging, Pager, MasterDetail } from 'devextreme-react/data-grid';
import { getLoanTypeShortName } from '../../loans/common/LoanFunctions';
import { WatchlistSummaryTypeOptions } from '@apply/constants';
import { handleAdminError } from '../../common/SharedFunctions';
import { onRenderWatchlistAddPortfolioGrid } from '../common/WatchlistFunctions';

const WatchlistAddLoanModal = () => {
    const { isOpen, watchlistId, summaryTypeId } = useSelector((state) => state.watchlists.watchlistAddLoanModal);

    const { dispatch } = store;
    const watchlistApi = new WatchlistApi.WatchlistApi(applicationInsights);

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        formColumn: { paddingBottom: '3px' },
        formRow: { marginBottom: '0' },
    };

    const [watchlist, set_watchlist] = useState(null);
    const [searchText, set_searchText] = useState(null);
    const [loans, set_loans] = useState([]);
    const [loadingMessage, set_loadingMessage] = useState('');

    const applySearch = (text) => {
        set_searchText(text);
    };

    const debouncedSearch = debounce(applySearch, 500);

    const _handleClose = () =>
        dispatch(
            watchlistActions.setWatchlistAddLoanModal({
                watchlistAddLoanModal: { isOpen: false },
            })
        );

    const loadWatchlistAsync = async (watchlistId) => {
        try {
            set_loadingMessage('Loading watchlist info...');
            const response = await watchlistApi.getWatchlistById(watchlistId);
            if (response.statusCode !== 200) {
                throw new Error('Failed to retrieve watchlist. Unexpected response.');
            }
            set_watchlist(response.result);
        } catch (ex) {
            handleAdminError(
                'An unexpected error occurred and watchlist info could no be loaded! Please try rfereshing the page.'
            );
        } finally {
            set_loadingMessage('');
        }
    };

    const searchLoansAsync = async (watchlistId, summaryTypeId, searchText) => {
        try {
            set_loadingMessage('Loading...');
            const response = await watchlistApi.getLoansNotInWatchlist(watchlistId, summaryTypeId, searchText);
            if (response.statusCode !== 200) {
                throw new Error('Failed to search loans. Unexpected response.');
            }
            set_loans(response.result);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error occurred. Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const addLoanToWatchlist = async (e, loanId) => {
        e.preventDefault();
        e.stopPropagation();

        if (!loanId) {
            return;
        }

        _handleClose();

        dispatch(
            watchlistActions.setWatchlistLoanDetailModal({
                watchlistLoanDetailModal: {
                    isOpen: true,
                    watchlistId,
                    loanId,
                },
            })
        );
    };

    const addLoanPropertyToWatchlist = async (e, propertyId) => {
        e.preventDefault();
        e.stopPropagation();

        if (!propertyId) {
            return;
        }

        _handleClose();

        dispatch(
            watchlistActions.setWatchlistLoanDetailModal({
                watchlistLoanDetailModal: {
                    isOpen: true,
                    watchlistId,
                    propertyId,
                },
            })
        );
    };

    useEffect(() => {
        if (watchlistId) {
            loadWatchlistAsync(watchlistId);
        }

        if (!summaryTypeId || !searchText) {
            return;
        }

        searchLoansAsync(watchlistId, summaryTypeId, searchText);
    }, [watchlistId, summaryTypeId, searchText]);

    const _onSearchChange = (event) => {
        debouncedSearch(event.target.value);
    };

    const actionCellRender = (e) => {
        return (
            <button
                id={'action_' + e.data.id}
                className='gridButton'
                title='Add Loan to Watchlist'
                onClick={(event) => addLoanToWatchlist(event, e.data.id)}
                disabled={!e.data.canAddToWatchlist}
            >
                Add
            </button>
        );
    };

    const onLoanTypeCellRender = (e) => {
        if (e.data.isRental) {
            return getLoanTypeShortName(LoanType.Rental);
        }
        return getLoanTypeShortName(e.data.loanTypeId);
    };

    const getSummaryTypeName = () => {
        var st = WatchlistSummaryTypeOptions.find((a) => a.id === summaryTypeId);
        return st?.name;
    };

    const onRowPrepared = (input) => {
        if (input?.rowType === 'data') {
            if (!input.data.portfolio || !(input.data.portfolio.length > 1)) {
                if (input.cells && input.cells.length > 0 && input.cells[0]?.cellElement) {
                    input.cells[0].cellElement.classList.remove('dx-datagrid-closed');
                    input.cells[0].cellElement.classList.remove('dx-datagrid-expand');
                    input.cells[0].cellElement.childNodes[0].classList.remove('dx-datagrid-group-closed');
                    input.cells[0].cellElement.childNodes[0].classList.remove('dx-datagrid-group-opened');
                }
            }
        }
    };

    if (!isOpen || !watchlistId || !summaryTypeId) {
        return '';
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '1100px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1 style={{ whiteSpace: 'nowrap' }}>Add Loan / Property to Watchlist</h1>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={_handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>

                                <div
                                    className='main-content'
                                    style={{
                                        padding: 0,
                                        backgroundColor: '#f4f4f4',
                                    }}
                                >
                                    <div
                                        style={{
                                            padding: '10px',
                                            textAlign: 'center',
                                            color: 'darkred',
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        &nbsp;&nbsp;Only Outstanding/Repaid {getSummaryTypeName()} loans not already included in
                                        the {watchlist?.description} watchlist will be returned in the search.
                                    </div>
                                    <div className='input-has-icon main-search'>
                                        <input
                                            type='search'
                                            placeholder='Search on BFI Number or Entity Name...'
                                            defaultValue={searchText}
                                            onChange={(event) => _onSearchChange(event)}
                                        />
                                    </div>
                                    {!searchText && (
                                        <div
                                            style={{
                                                padding: '10px',
                                                textAlign: 'center',
                                                color: 'darkblue',
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faInfoCircle} />
                                            &nbsp;&nbsp;Enter some text into the search box above...
                                        </div>
                                    )}
                                </div>

                                <div
                                    className='scrollable-content'
                                    style={{ padding: '1rem', height: 'auto' }}
                                >
                                    {searchText && (
                                        <DataGrid
                                            dataSource={loans}
                                            applicationInsights={applicationInsights}
                                            noDataText='No loans found meeting your criteria'
                                            rowAlternationEnabled={true}
                                            showColumnHeaders={true}
                                            showRowLines={true}
                                            showBorders={true}
                                            onRowPrepared={onRowPrepared}
                                        >
                                            <Column
                                                dataField='bfiNumber'
                                                caption='BFI #'
                                                dataType='string'
                                                width={120}
                                            />
                                            <Column
                                                dataField='loanTypeId'
                                                caption='Type'
                                                cellRender={onLoanTypeCellRender}
                                                alignment='left'
                                                width={80}
                                            />
                                            <Column
                                                dataField='borrowerEntity'
                                                caption='Entity'
                                            />
                                            <Column
                                                dataField='propertyState'
                                                caption='Prop. State'
                                                width={120}
                                            />
                                            <Column
                                                dataField='committed'
                                                caption='Committed'
                                                dataType='number'
                                                format='currency'
                                                width={90}
                                                alignment={'left'}
                                            />
                                            <Column
                                                alignment='center'
                                                cellRender={actionCellRender}
                                                width={70}
                                                allowReordering={false}
                                                allowSorting={false}
                                                allowResizing={false}
                                            />
                                            <MasterDetail
                                                enabled={true}
                                                render={(e) => onRenderWatchlistAddPortfolioGrid(e, addLoanPropertyToWatchlist)}
                                            />
                                            <Paging pageSize={10} />
                                            <Pager
                                                showPageSizeSelector={true}
                                                allowedPageSizes={[10]}
                                                showNavigationButtons={true}
                                                showInfo={true}
                                            />
                                        </DataGrid>
                                    )}
                                </div>

                                <div
                                    className='standard-form no-search'
                                    style={{ padding: '0', margin: '0' }}
                                >
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={_handleClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
        </>
    );
};

export default WatchlistAddLoanModal;
